import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle, Pencil, X, Check, ChevronDown } from 'lucide-react';

// Comprehensive list of countries
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'England', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi',
  'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova',
  'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia',
  'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Scotland', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Wales', 'Yemen', 'Zambia', 'Zimbabwe'
];

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

// Country Autocomplete Component
const CountryAutocomplete = ({ value, onChange, placeholder = "Select or type a country..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [filteredCountries, setFilteredCountries] = useState(COUNTRIES);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const filtered = COUNTRIES.filter(country =>
      country.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCountries(filtered);
    setHighlightedIndex(-1);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleCountrySelect = (country) => {
    setSearchTerm(country);
    onChange(country);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
          handleCountrySelect(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-4 py-4 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-gray-700 min-h-[60px] transition-all duration-200"
          autoComplete="off"
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDown className={`h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredCountries.length > 0 ? (
            filteredCountries.map((country, index) => (
              <button
                key={country}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
                  index === highlightedIndex ? 'bg-blue-50' : ''
                } ${searchTerm === country ? 'bg-blue-100 font-medium' : ''}`}
                onClick={() => handleCountrySelect(country)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {country}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 italic">
              No countries found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Profile({ user }) {
  const [profileData, setProfileData] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editDraftPills, setEditDraftPills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ field: '', type: '', text: '' });

  useEffect(() => {
    async function fetchProfile() {
      if (!user) return;
      try {
        const response = await fetch(`${API_BASE}/v1/users/${user.id}`, {
          headers: { 'X-API-Key': API_KEY }
        });
        if (response.ok) {
          const data = await response.json();
          setProfileData(data);
        } else {
          setMessage({ type: 'error', text: 'Failed to load profile data' });
        }
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to load profile data' });
      }
    }
    fetchProfile();
  }, [user]);

  // Helper function to convert comma-separated string to array
  const pillStringToArray = str =>
    str && str.trim().length ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

  function startEditingField(field) {
    setEditingField(field);
    setMessage({ field: '', type: '', text: '' });
    
    if (field === 'services') {
      setEditDraftPills(pillStringToArray(profileData?.[field] || ''));
      setEditDraft('');
    } else {
      setEditDraft(profileData?.[field] || '');
    }
  }

  async function handleSave() {
    if (!user || !editingField) return;
    setSaving(true);
    
    let valueToSave;
    if (editingField === 'services') {
      let pills = [...editDraftPills];
      if (editDraft.trim()) {
        pills = [...pills, editDraft.trim()];
      }
      valueToSave = pills.join(', ');
      setEditDraftPills(pills);
      setEditDraft('');
    } else {
      valueToSave = editDraft;
    }

    try {
      const bodyData = { [editingField]: valueToSave };
      const response = await fetch(`${API_BASE}/v1/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        body: JSON.stringify(bodyData)
      });
      if (response.ok) {
        setProfileData(prev => ({ ...prev, [editingField]: valueToSave }));
        setMessage({ field: editingField, type: 'success', text: 'Saved!' });
        setEditingField(null);
        setTimeout(() => setMessage({ field: '', type: '', text: '' }), 1500);
      } else {
        const err = await response.json();
        setMessage({ field: editingField, type: 'error', text: err.detail || 'Save failed' });
      }
    } catch (err) {
      setMessage({ field: editingField, type: 'error', text: 'Save failed' });
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setEditingField(null);
    setMessage({ field: '', type: '', text: '' });
  }

  // Render services as pills
  const renderServicesPills = (servicesString) => {
    const services = pillStringToArray(servicesString);
    if (!services.length) {
      return <span className="text-gray-400 italic">No services listed</span>;
    }
    
    return (
      <div className="flex flex-wrap gap-2">
        {services.map((service, idx) => (
          <span
            key={idx}
            className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full text-sm font-medium shadow-sm"
          >
            {service}
          </span>
        ))}
      </div>
    );
  };

  // Render pill input for services
  const renderPillInput = () => (
    <div
      className="flex flex-wrap items-center border border-gray-300 rounded-lg px-3 py-2 min-h-[48px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200 bg-white"
      onClick={() => document.getElementById('services-input')?.focus()}
    >
      {editDraftPills.map((service, idx) => (
        <span
          key={idx}
          className="flex items-center px-3 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full text-sm font-medium mr-2 mb-2 shadow-sm"
        >
          {service}
          <button
            className="ml-2 text-white/80 hover:text-white focus:outline-none font-bold text-lg leading-none"
            onClick={e => {
              e.stopPropagation();
              const newPills = editDraftPills.filter((_, i) => i !== idx);
              setEditDraftPills(newPills);
            }}
            type="button"
            aria-label={`Remove service: ${service}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id="services-input"
        type="text"
        value={editDraft}
        placeholder={editDraftPills.length === 0 ? "E.g. Web Development, Design, Consulting" : ''}
        className="flex-1 outline-none border-none bg-transparent min-w-[120px] py-1 text-sm placeholder-gray-400"
        onChange={e => {
          const raw = e.target.value;
          if (raw.includes(',')) {
            const parts = raw.split(',');
            const toAdd = parts.slice(0, -1).map(s => s.trim()).filter(Boolean);
            const last = parts[parts.length - 1];
            setEditDraftPills([...editDraftPills, ...toAdd]);
            setEditDraft(last);
          } else {
            setEditDraft(raw);
          }
        }}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === 'Tab') && editDraft.trim() !== '') {
            e.preventDefault();
            setEditDraftPills([...editDraftPills, editDraft.trim()]);
            setEditDraft('');
          }
        }}
        maxLength={500}
      />
    </div>
  );

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg border p-8 text-center">
          <p className="text-gray-600">Please login to see your profile.</p>
        </div>
      </div>
    );
  }

  // Skeleton loading component
  const ProfileSkeleton = () => (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="h-9 bg-gray-200 rounded-lg w-48 mb-8 animate-pulse"></div>
          
          <div className="space-y-8">
            {[1, 2, 3, 4, 5].map((item, index) => (
              <div key={item} className="relative">
                <div className="mb-3">
                  <div className="h-5 bg-gray-200 rounded w-24 animate-pulse"></div>
                </div>
                
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 min-h-[60px]">
                  {index === 4 ? (
                    <div className="flex flex-wrap gap-2">
                      <div className="h-7 bg-gray-300 rounded-full w-20 animate-pulse"></div>
                      <div className="h-7 bg-gray-300 rounded-full w-16 animate-pulse"></div>
                      <div className="h-7 bg-gray-300 rounded-full w-24 animate-pulse"></div>
                    </div>
                  ) : index === 2 ? (
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-300 rounded w-full animate-pulse"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
                      <div className="h-4 bg-gray-300 rounded w-1/2 animate-pulse"></div>
                    </div>
                  ) : (
                    <div className="h-4 bg-gray-300 rounded w-2/3 animate-pulse"></div>
                  )}
                </div>
                
                {index < 4 && (
                  <div className="mt-8 border-b border-gray-100"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (!profileData) {
    return <ProfileSkeleton />;
  }

  const fields = [
    {
      key: 'username',
      label: 'Username',
      renderValue: v => v || <span className="text-gray-400 italic">Not set</span>,
      editable: true
    },
    {
      key: 'email',
      label: 'Email',
      renderValue: v => v || <span className="text-gray-400 italic">No email provided</span>,
      editable: false
    },
    {
      key: 'bio',
      label: 'Bio',
      renderValue: v => v || <span className="text-gray-400 italic">Not set</span>,
      editable: true,
      isTextarea: true
    },
    {
      key: 'location', // Keep as 'location' for backend compatibility
      label: 'Country',
      renderValue: v => v || <span className="text-gray-400 italic">Not set</span>,
      renderEdit: () => (
        <CountryAutocomplete 
          value={editDraft} 
          onChange={setEditDraft}
          placeholder="Select or type your country..."
        />
      ),
      editable: true
    },
    {
      key: 'services',
      label: 'Services',
      renderValue: v => renderServicesPills(v),
      renderEdit: renderPillInput,
      editable: true
    }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="p-6 sm:p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Manage your profile</h2>
          
          <div className="space-y-8">
            {fields.map((field, index) => (
              <div key={field.key} className="relative group">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    {field.label}
                  </h3>
                </div>
                
                {editingField !== field.key ? (
                  <>
                    <div 
                      className={`relative bg-gray-50 rounded-xl border border-gray-200 p-4 min-h-[60px] group-hover:border-gray-300 transition-all duration-200 ${
                        field.editable ? 'cursor-pointer' : ''
                      }`}
                      onClick={field.editable ? () => startEditingField(field.key) : undefined}
                    >
                      <div className="text-gray-700 break-words whitespace-pre-line leading-relaxed pr-12">
                        {field.renderValue(profileData[field.key])}
                      </div>
                      {field.editable && (
                        <div className="absolute top-3 right-3 text-gray-400 group-hover:text-blue-600 transition-all duration-200 p-1.5 rounded-lg pointer-events-none">
                          <Pencil className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    {(message.field === field.key && message.type) && (
                      <div className={`flex items-center gap-2 mt-3 p-3 rounded-lg text-sm font-medium ${
                        message.type === 'success'
                          ? 'text-green-700 bg-green-50 border border-green-200'
                          : 'text-red-700 bg-red-50 border border-red-200'
                        }`}>
                        {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        <span>{message.text}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-4">
                    {field.renderEdit ? (
                      field.renderEdit()
                    ) : field.isTextarea ? (
                      <textarea
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value)}
                        placeholder="Type here..."
                        rows={4}
                        className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white resize-none text-gray-700 leading-relaxed transition-all duration-200"
                        maxLength={1000}
                        autoFocus
                      />
                    ) : (
                      <input
                        type={field.key === 'email' ? 'email' : 'text'}
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value)}
                        placeholder="Type here..."
                        className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-gray-700 min-h-[60px] transition-all duration-200"
                        maxLength={500}
                        autoFocus
                      />
                    )}
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        className="flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-5 py-2 font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-200 transition-all duration-200 disabled:opacity-50 text-sm"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        <Check className="w-4 h-4" /> 
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="flex items-center justify-center gap-2 bg-gray-200 text-gray-800 rounded-lg px-5 py-2 font-medium hover:bg-gray-300 transition-all duration-200 text-sm"
                        onClick={handleCancel}
                      >
                        <X className="w-4 h-4" /> 
                        Cancel
                      </button>
                    </div>
                    
                    {(message.field === field.key && message.type) && (
                      <div className={`flex items-center gap-2 p-3 rounded-lg text-sm font-medium ${
                        message.type === 'success'
                          ? 'text-green-700 bg-green-50 border border-green-200'
                          : 'text-red-700 bg-red-50 border border-red-200'
                        }`}>
                        {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        <span>{message.text}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {index < fields.length - 1 && (
                  <div className="mt-8 border-b border-gray-100"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}